import { PropsWithChildren, useContext, useEffect } from 'react';
import type { Ref } from 'react';
import { cn } from '@ifixit/ui/lib/utils';
import { KeypressContext } from '../Providers/keypressContext';
import { MinimalDisclosure } from 'Guide/Schematics/Diagram/Logic/minimalDisclosure';

const simulateClickOnActiveEl = () => {
   const event = new MouseEvent('click', {
      view: window,
      bubbles: true,
      cancelable: true,
      buttons: 1,
   });
   document.activeElement?.dispatchEvent(event);
};

// Default stacking layer. The THD work-order result modal opens on top of an
// already-open diagram lightbox (another Modal), so it passes a higher level to
// keep its own blurred backdrop above the lightbox content instead of colliding
// with it at the same z-index.
const DEFAULT_OVERLAY_Z = 'z-[9998]';
const DEFAULT_CONTENT_Z = 'z-[9999]';

function Modal({
   ref,
   isOpen,
   onClose,
   children,
   overlayClassName = DEFAULT_OVERLAY_Z,
   contentClassName = DEFAULT_CONTENT_Z,
}: PropsWithChildren<MinimalDisclosure> & {
   ref?: Ref<HTMLDivElement>;
   overlayClassName?: string;
   contentClassName?: string;
}) {
   const keypressContext = useContext(KeypressContext);

   useEffect(() => {
      const mappings = [
         { id: 'Modal', key: 'Escape', action: onClose },
         { id: 'Modal', key: ' ', action: simulateClickOnActiveEl },
      ];
      // @ts-expect-error TS(2345) FIXME: Argument of type '{ id: string; key: string; actio... Remove this comment to see the full error message
      keypressContext.pushMappings(mappings);
      return () => {
         // @ts-expect-error TS(2345) FIXME: Argument of type '{ id: string; key: string; actio... Remove this comment to see the full error message
         keypressContext.popMappings(mappings);
      };
   }, [keypressContext.pushMappings, keypressContext.popMappings, onClose]);

   // Block scroll when modal is open
   useEffect(() => {
      if (isOpen) {
         document.body.style.overflow = 'hidden';
         return () => {
            document.body.style.overflow = '';
         };
      }
   }, [isOpen]);

   if (!isOpen) {
      return null;
   }

   return (
      <>
         {/* Overlay */}
         <div
            className={cn('fixed inset-0 backdrop-blur-[10px]', overlayClassName)}
            style={{ backgroundColor: 'rgba(227, 232, 237, 0.75)' }}
            onClick={onClose}
            onKeyDown={e => {
               if (e.key === 'Escape' && onClose) {
                  onClose();
               }
            }}
         />
         {/* Content */}
         <div
            className={cn(
               'fixed inset-0 w-screen h-screen bg-transparent outline-none',
               contentClassName
            )}
            role="dialog"
            aria-modal="true"
         >
            <div ref={ref} className="w-full h-full p-0 m-0 overflow-auto">
               {children}
            </div>
         </div>
      </>
   );
}

export default Modal;
